"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  // Without a reference on screen, "Something went wrong" is unreportable: an
  // acceptance tester can describe the symptom but nothing ties it to the
  // Sentry event or the server-side digest that holds the real cause. Show
  // both (never the raw message — that can carry request internals).
  const [reference, setReference] = useState<string>("");

  useEffect(() => {
    const eventId = Sentry.captureException(error);
    setReference(error.digest || eventId || "");
  }, [error]);

  const handleGoHome = () => {
    router.push("/");
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: "40px 20px",
        textAlign: "center",
        fontFamily: "var(--font-inter), sans-serif",
        background: "#f7f2ee",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          padding: "40px",
          boxShadow: "0px 0px 16px 0px rgba(0, 0, 0, 0.14)",
          maxWidth: "400px",
          width: "100%",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "#fef2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c10505"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h2
          style={{
            color: "#2f3033",
            fontSize: "24px",
            fontWeight: 600,
            margin: "0 0 12px",
          }}
        >
          Something went wrong
        </h2>

        <p
          style={{
            color: "#757575",
            fontSize: "14px",
            margin: "0 0 24px",
            lineHeight: "1.5",
          }}
        >
          We encountered an unexpected error. Please try again.
        </p>

        {reference && (
          <p
            style={{
              color: "#9a9a9a",
              fontSize: "12px",
              fontFamily: "monospace",
              margin: "0 0 20px",
              wordBreak: "break-all",
            }}
          >
            Reference: {reference}
          </p>
        )}

        <button
          onClick={() => reset()}
          style={{
            background: "#ffffff",
            color: "#c10505",
            border: "1px solid #c10505",
            borderRadius: "12px",
            padding: "15px 24px",
            fontSize: "16px",
            fontWeight: 600,
            cursor: "pointer",
            width: "100%",
            marginBottom: "12px",
            fontFamily: "var(--font-inter), sans-serif",
          }}
        >
          Try again
        </button>

        <button
          onClick={handleGoHome}
          style={{
            background: "#c10505",
            color: "#ffffff",
            border: "none",
            borderRadius: "12px",
            padding: "15px 24px",
            fontSize: "16px",
            fontWeight: 600,
            cursor: "pointer",
            width: "100%",
            fontFamily: "var(--font-inter), sans-serif",
          }}
        >
          Go to Homepage
        </button>
      </div>
    </div>
  );
}
